import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import api from '../../utils/axios';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Wallet, RefreshCw, Download, Eye, Search, ChevronUp, ChevronDown,
  Filter, Settings2, Loader2, CheckCircle2, AlertCircle, ChevronsLeft, ChevronLeft,
  ChevronRight, ChevronsRight, ExternalLink, PlayCircle, XCircle, Link as LinkIcon, Upload,
} from 'lucide-react';
import { BuscarMovimientosDialog } from './busqueda/BuscarMovimientosDialog';
import { BusquedaMovimientosPanel } from './busqueda/BusquedaMovimientosPanel';

// ============================================
// Columnas
// ============================================
const COLUMNS = [
  { key: 'nombre', label: 'Nombre / Razón Social', width: 220, sortable: true },
  { key: 'email', label: 'Email', width: 200, sortable: true },
  { key: 'dni', label: 'DNI', width: 110, sortable: true },
  { key: 'cuit', label: 'CUIT', width: 130, sortable: true },
  { key: 'cvu', label: 'CVU', width: 200, sortable: true },
  { key: 'movimiento', label: 'Último Movimiento', width: 140, sortable: true },
  { key: 'estado_usuario', label: 'Estado', width: 110, sortable: true, render: (v) => (
      <Badge className={v === 'Habilitado' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>{v}</Badge>
    ) },
  { key: 'limite_diario', label: 'Lím. Diario', width: 130, sortable: true, sortKey: 'limite_diario_num', align: 'right' },
  { key: 'limite_mensual', label: 'Lím. Mensual', width: 140, sortable: true, sortKey: 'limite_mensual_num', align: 'right' },
  { key: 'total_disponible', label: 'Total Disponible', width: 140, sortable: true, sortKey: 'total_disponible_num', align: 'right' },
  { key: 'validacion_identidad', label: 'Val. Identidad', width: 120, sortable: true },
  // Paso 2 - saldos
  { key: 'ultimo_saldo_fecha', label: 'Fecha Saldo (P2)', width: 130, sortable: true, sortKey: '_saldo_fecha_iso', extra: true,
    get: (r) => r.ultimo_saldo?.fecha || '' },
  { key: 'ultimo_saldo_monto', label: 'Saldo al Momento (P2)', width: 170, sortable: true, sortKey: '_saldo_monto_num', extra: true, align: 'right',
    get: (r) => r.ultimo_saldo?.saldo_al_momento || '',
    render: (v, r) => {
      const n = r.ultimo_saldo?.saldo_al_momento_num;
      if (v == null || v === '') {
        if (r.ultimo_saldo?.found === false) return <span className="text-xs text-slate-400 italic">sin movs</span>;
        return <span className="text-xs text-slate-300">—</span>;
      }
      const cls = (n != null && n < 0) ? 'text-red-600 font-semibold' : 'text-slate-700';
      return <span className={cls}>{v}</span>;
    },
  },
];

const DEFAULT_VISIBLE = COLUMNS.reduce((acc, c) => { acc[c.key] = true; return acc; }, {});

// Helpers
const fmtAR = (d) => {
  if (!d) return '';
  try {
    return new Date(d).toLocaleString('es-AR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return d; }
};
const fmtDateISO = (ddmmyyyy) => {
  // 'dd/MM/yyyy HH:mm' -> ISO sortable
  if (!ddmmyyyy) return '';
  const m = String(ddmmyyyy).match(/(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}))?/);
  if (!m) return ddmmyyyy;
  return `${m[3]}-${m[2]}-${m[1]}T${m[4] || '00'}:${m[5] || '00'}`;
};

const downloadCSV = (rows, visibleCols, filename) => {
  const cols = COLUMNS.filter(c => visibleCols[c.key]);
  const header = cols.map(c => c.label).join(';');
  const escape = (s) => {
    if (s == null) return '';
    const v = String(s).replace(/"/g, '""');
    return /[;\n"\r]/.test(v) ? `"${v}"` : v;
  };
  const lines = rows.map(r => cols.map(c => {
    const v = c.get ? c.get(r) : r[c.key];
    return escape(v);
  }).join(';'));
  const csv = '\uFEFF' + [header, ...lines].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Parsea '$ 1.234.567,89' / '$ -3.200.000,00' -> number (o null)
const parseMoney = (s) => {
  if (s == null) return null;
  let t = String(s).replace(/\$/g, '').replace(/\s/g, '');
  if (!t) return null;
  const neg = t.startsWith('-') || (t.startsWith('(') && t.endsWith(')'));
  t = t.replace(/[()\-]/g, '').replace(/\./g, '').replace(',', '.');
  const v = parseFloat(t);
  if (Number.isNaN(v)) return null;
  return neg ? -v : v;
};

// Parser CSV robusto (campos entre comillas, comas internas, comillas escapadas "")
const parseCSVText = (text) => {
  const rows = [];
  let field = '', row = [], inQuotes = false;
  const src = text.charCodeAt(0) === 0xFEFF ? text.slice(1) : text; // quitar BOM
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else { field += ch; }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(field); field = '';
    } else if (ch === '\n') {
      row.push(field); rows.push(row); row = []; field = '';
    } else if (ch !== '\r') {
      field += ch;
    }
  }
  if (field !== '' || row.length) { row.push(field); rows.push(row); }
  return rows;
};

// ============================================
// Componente principal
// ============================================
const PAGE_SIZE_OPTIONS = [25, 50, 100, 250, 500];

export default function SaldosCuenta() {
  const [snapshot, setSnapshot] = useState(null);
  const [loadingSnap, setLoadingSnap] = useState(true);
  const [syncJob, setSyncJob] = useState(null);
  const [syncJobId, setSyncJobId] = useState(null);

  const [saldoJob, setSaldoJob] = useState(null);
  // Modo de ejecución: 'local' (backend del pod) | 'external' (agente E1 desde preview)
  const [execMode, setExecMode] = useState(() => {
    try { return localStorage.getItem('saldos_exec_mode') || 'local'; } catch { return 'local'; }
  });
  const [externalJobs, setExternalJobs] = useState([]);

  const [visibleCols, setVisibleCols] = useState(DEFAULT_VISIBLE);
  const [search, setSearch] = useState('');
  const [cvuFilter, setCvuFilter] = useState('todas');
  const [buscarOpen, setBuscarOpen] = useState(false);
  const [busquedaNueva, setBusquedaNueva] = useState(null);
  const [sortBy, setSortBy] = useState({ key: 'nombre', dir: 'asc' });
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(new Set());

  // Polling refs
  const syncPollRef = useRef(null);
  const saldoPollRef = useRef(null);
  const importFileRef = useRef(null);

  // Estado de importación del CSV del script PowerShell
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ done: 0, total: 0 });

  // -------- Cargar snapshot latest --------
  const loadLatest = useCallback(async () => {
    setLoadingSnap(true);
    try {
      const r = await api.get('/reportes/saldos/latest');
      if (r.data?.exists) {
        // Enriquecer cada item con campos derivados para sort
        const items = (r.data.items || []).map(it => ({
          ...it,
          _saldo_fecha_iso: fmtDateISO(it.ultimo_saldo?.fecha),
          _saldo_monto_num: it.ultimo_saldo?.saldo_al_momento_num ?? null,
        }));
        setSnapshot({ ...r.data, items });
      } else {
        setSnapshot(null);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingSnap(false);
    }
  }, []);

  // -------- Cargar saldo-job actual (por si quedó uno corriendo) --------
  const loadSaldoJobActual = useCallback(async () => {
    try {
      const r = await api.get('/reportes/saldos/saldo-job-actual');
      if (r.data?.exists) {
        setSaldoJob(r.data.job);
        if (r.data.active) startSaldoPolling(r.data.job.id);
      }
    } catch { /* ignore */ }
  }, []);

  // -------- Cargar sync-job activo (por si está corriendo de antes) --------
  const loadSyncJobActual = useCallback(async () => {
    try {
      // Buscamos un sync activo intentando crear uno nuevo: si hay uno en progreso devuelve reused:true
      // O mejor: el endpoint /sync ya lo hace, pero querríamos solo lectura. Hacemos un truco:
      // No hay endpoint puro lectura, así que solo nos enteramos cuando el usuario aprieta sync.
      // Para detectar zombies, llamamos a /sync con un POST de prueba? No, eso es invasivo.
      // En cambio, agregamos endpoint dedicado.
      const r = await api.get('/reportes/saldos/sync-job-actual').catch(() => null);
      if (r?.data?.exists && r.data.active) {
        setSyncJob(r.data.job);
        setSyncJobId(r.data.job.id);
        startSyncPolling(r.data.job.id);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    loadLatest();
    loadSaldoJobActual();
    loadSyncJobActual();
    loadExternalJobs();
    // Refresh external jobs cada 15s
    const ej = setInterval(loadExternalJobs, 15000);
    return () => {
      if (syncPollRef.current) clearInterval(syncPollRef.current);
      if (saldoPollRef.current) clearInterval(saldoPollRef.current);
      clearInterval(ej);
    };
    // eslint-disable-next-line
  }, []);

  // -------- Abortar / Reiniciar jobs colgados --------
  const abortJobs = async () => {
    if (!window.confirm('¿Cancelar el sync en curso? Esto marca el job actual como ERROR y te permite arrancar uno nuevo.')) return;
    try {
      const r = await api.post('/reportes/saldos/abort');
      toast.success(`Jobs abortados: ${r.data.abortados}`);
      if (syncPollRef.current) { clearInterval(syncPollRef.current); syncPollRef.current = null; }
      if (saldoPollRef.current) { clearInterval(saldoPollRef.current); saldoPollRef.current = null; }
      setSyncJob(prev => prev ? { ...prev, estado: 'ERROR', error: 'abortado_por_usuario' } : null);
      setSaldoJob(prev => prev ? { ...prev, estado: 'ERROR', error: 'abortado_por_usuario' } : null);
    } catch (e) {
      toast.error('No se pudo abortar');
    }
  };

  // Persistir modo
  useEffect(() => {
    try { localStorage.setItem('saldos_exec_mode', execMode); } catch { /* ignore */ }
  }, [execMode]);

  // Cargar jobs externos recientes
  const loadExternalJobs = useCallback(async () => {
    try {
      const r = await api.get('/reportes/saldos/external/all?limit=10');
      setExternalJobs(r.data?.jobs || []);
    } catch { /* ignore */ }
  }, []);

  // -------- Sync Paso 1 --------
  const startSync = async () => {
    if (execMode === 'external') {
      try {
        const r = await api.post('/reportes/saldos/external/queue', {
          tipo: 'SYNC',
          payload: {},
        });
        toast.success(`Trabajo SYNC encolado para agente externo (id ${r.data.job_id.slice(0, 8)})`);
        loadExternalJobs();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Error encolando');
      }
      return;
    }
    // Modo local
    try {
      const r = await api.post('/reportes/saldos/sync', { timeout_min: 8 });
      const id = r.data?.job_id;
      if (!id) return;
      setSyncJobId(id);
      if (r.data?.reused) {
        toast.info('Ya hay una sincronización en curso. Reuso.');
      } else {
        toast.success('Sincronización iniciada. Puede tardar ~2 min...');
      }
      startSyncPolling(id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al iniciar sincronización');
    }
  };

  const startSyncPolling = (id) => {
    if (syncPollRef.current) clearInterval(syncPollRef.current);
    syncPollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/reportes/saldos/job/${id}`);
        setSyncJob(r.data);
        if (['COMPLETADO', 'ERROR'].includes(r.data?.estado)) {
          clearInterval(syncPollRef.current);
          syncPollRef.current = null;
          if (r.data.estado === 'COMPLETADO') {
            toast.success(`Sincronización completa: ${r.data.total} billeteras`);
            loadLatest();
          } else {
            toast.error(`Error: ${r.data.error || 'desconocido'}`);
          }
        }
      } catch { /* ignore */ }
    }, 5000);
  };

  // -------- Saldo lote (Paso 2) --------
  const startSaldoLote = async (ids) => {
    if (!snapshot) {
      toast.error('Sin snapshot. Sincronizá primero.');
      return;
    }
    if (!ids.length) {
      toast.warning('Sin billeteras para procesar');
      return;
    }
    if (execMode === 'external') {
      try {
        const r = await api.post('/reportes/saldos/external/queue', {
          tipo: 'SALDO_LOTE',
          payload: {
            ids,
            dias_atras: 30,
            snapshot_id: snapshot.id,
          },
        });
        toast.success(`Lote de ${ids.length} encolado para agente externo (id ${r.data.job_id.slice(0, 8)})`);
        loadExternalJobs();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Error encolando');
      }
      return;
    }
    // Modo local
    try {
      const r = await api.post('/reportes/saldos/saldo-job', {
        snapshot_id: snapshot.id,
        ids,
        dias_atras: 30,
      });
      if (r.data?.reused) {
        toast.info('Ya hay un job de saldo en curso. Mostrando progreso.');
      } else {
        toast.success(`Procesando ${ids.length} billeteras en background...`);
      }
      startSaldoPolling(r.data.job_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error iniciando saldo job');
    }
  };

  const startSaldoPolling = (id) => {
    if (saldoPollRef.current) clearInterval(saldoPollRef.current);
    saldoPollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/reportes/saldos/saldo-job/${id}`);
        setSaldoJob(r.data);
        // NO recargamos el snapshot durante el job — eso satura el backend.
        // Se recarga solo cuando termina.
        if (['COMPLETADO', 'ERROR'].includes(r.data?.estado)) {
          clearInterval(saldoPollRef.current);
          saldoPollRef.current = null;
          await loadLatest();
          if (r.data.estado === 'COMPLETADO') {
            toast.success(`Saldos completados: OK=${r.data.ok} · sin movs=${r.data.vacios} · errores=${r.data.errores}`);
          } else {
            toast.error(`Saldo job falló: ${r.data.error || 'desconocido'}`);
          }
        }
      } catch { /* ignore */ }
    }, 4000);
  };

  // -------- Filtrar + ordenar + paginar --------
  const items = snapshot?.items || [];
  const filtered = useMemo(() => {
    const porCvu = cvuFilter === 'todas' ? items
      : items.filter(it => (cvuFilter === 'con') === !!(it.cvu || '').trim());
    if (!search.trim()) return porCvu;
    const q = search.toLowerCase().trim();
    return porCvu.filter(it =>
      (it.nombre || '').toLowerCase().includes(q) ||
      (it.email || '').toLowerCase().includes(q) ||
      (it.cuit || '').toLowerCase().includes(q) ||
      (it.dni || '').toLowerCase().includes(q) ||
      (it.cvu || '').toLowerCase().includes(q)
    );
  }, [items, search, cvuFilter]);

  const sorted = useMemo(() => {
    if (!sortBy.key) return filtered;
    const col = COLUMNS.find(c => c.key === sortBy.key);
    const sortKey = col?.sortKey || sortBy.key;
    const getVal = (r) => {
      if (col?.get && !col.sortKey) return col.get(r);
      const v = r[sortKey];
      return v == null ? '' : v;
    };
    const arr = [...filtered].sort((a, b) => {
      const va = getVal(a), vb = getVal(b);
      const na = typeof va === 'number' ? va : (parseFloat(va) || null);
      const nb = typeof vb === 'number' ? vb : (parseFloat(vb) || null);
      let cmp;
      if (na !== null && nb !== null && !isNaN(na) && !isNaN(nb) && typeof va !== 'string') {
        cmp = na - nb;
      } else {
        cmp = String(va).localeCompare(String(vb), 'es-AR', { numeric: true });
      }
      return sortBy.dir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sortBy]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const pageItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, page, pageSize]);

  useEffect(() => { setPage(1); }, [search, pageSize, sortBy]);

  // -------- Selección --------
  const toggleSel = (id) => {
    if (!id) return;
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const togglePageSel = () => {
    const idsPage = pageItems.map(i => i.id_billetera).filter(Boolean);
    const allSelected = idsPage.every(id => selected.has(id));
    setSelected(prev => {
      const n = new Set(prev);
      if (allSelected) idsPage.forEach(id => n.delete(id));
      else idsPage.forEach(id => n.add(id));
      return n;
    });
  };
  const clearSel = () => setSelected(new Set());
  const selectFiltered = () => {
    setSelected(new Set(sorted.map(i => i.id_billetera).filter(Boolean)));
  };
  const selectFirstN = (n) => {
    setSelected(new Set(sorted.slice(0, n).map(i => i.id_billetera).filter(Boolean)));
  };

  // -------- Sorting click --------
  const onHeaderClick = (col) => {
    if (!col.sortable) return;
    setSortBy(prev => {
      if (prev.key === col.key) {
        return { key: col.key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      return { key: col.key, dir: 'asc' };
    });
  };

  // -------- Export --------
  const exportCSV = () => {
    if (!sorted.length) {
      toast.warning('Sin datos para exportar');
      return;
    }
    const ts = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');
    downloadCSV(sorted, visibleCols, `saldos_billeteras_${ts}.csv`);
    toast.success(`Exportadas ${sorted.length} filas`);
  };

  // Descarga un CSV con los links directos a /MovimientosBilletera de cada billetera.
  // Pensado para consumirse desde el script PowerShell (waya_saldos.ps1).
  const downloadLinks = () => {
    if (!items.length) {
      toast.warning('Sin billeteras. Sincronizá primero (Paso 1).');
      return;
    }
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = ['id_billetera', 'nombre', 'cuit', 'cvu', 'link'].join(',');
    const lines = items
      .filter(it => it.id_billetera)
      .map(it => [
        esc(it.id_billetera),
        esc(it.nombre),
        esc(it.cuit),
        esc(it.cvu),
        esc(`https://administracion.waya.com.ar/MovimientosBilletera?IdBilletera=${it.id_billetera}`),
      ].join(','));
    const csv = '\uFEFF' + [header, ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `waya_links_billeteras.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${lines.length} links exportados`);
  };

  // Importa el CSV de salida del script PowerShell (waya_saldos.csv) y mergea
  // los saldos sobre el snapshot vigente (setea items.$.ultimo_saldo).
  // Columnas esperadas: id_billetera, estado, fecha, descripcion, monto, saldo_al_momento
  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = ''; // permitir re-seleccionar el mismo archivo
    if (!file) return;
    if (!snapshot?.id) {
      toast.error('No hay snapshot. Sincronizá primero (Paso 1).');
      return;
    }
    setImporting(true);
    setImportProgress({ done: 0, total: 0 });
    try {
      const text = await file.text();
      const rows = parseCSVText(text);
      if (rows.length < 2) {
        toast.error('El CSV está vacío o no tiene filas de datos.');
        setImporting(false);
        return;
      }
      const header = rows[0].map(h => h.trim().toLowerCase());
      const idx = {
        id: header.indexOf('id_billetera'),
        estado: header.indexOf('estado'),
        fecha: header.indexOf('fecha'),
        desc: header.indexOf('descripcion'),
        monto: header.indexOf('monto'),
        saldo: header.indexOf('saldo_al_momento'),
      };
      if (idx.id === -1) {
        toast.error("El CSV no tiene columna 'id_billetera'. ¿Es el archivo correcto?");
        setImporting(false);
        return;
      }

      const saldos = [];
      for (let r = 1; r < rows.length; r++) {
        const cols = rows[r];
        const idb = (cols[idx.id] || '').trim();
        if (!idb) continue;
        const estado = (idx.estado !== -1 ? cols[idx.estado] : '').trim().toUpperCase();
        const fecha = idx.fecha !== -1 ? (cols[idx.fecha] || '').trim() : '';
        const descripcion = idx.desc !== -1 ? (cols[idx.desc] || '').trim() : '';
        const monto = idx.monto !== -1 ? (cols[idx.monto] || '').trim() : '';
        const saldo = idx.saldo !== -1 ? (cols[idx.saldo] || '').trim() : '';
        saldos.push({
          id_billetera: idb,
          found: estado === 'OK',
          fecha: fecha || null,
          descripcion: descripcion || null,
          monto: monto || null,
          monto_num: parseMoney(monto),
          saldo_al_momento: saldo || null,
          saldo_al_momento_num: parseMoney(saldo),
          error: estado === 'ERROR' ? 'error_scraping' : null,
        });
      }

      if (!saldos.length) {
        toast.error('No se encontraron filas válidas en el CSV.');
        setImporting(false);
        return;
      }

      // POST en lotes para no mandar un payload gigante
      const BATCH = 1000;
      let actualizadas = 0, noEncontradas = 0;
      setImportProgress({ done: 0, total: saldos.length });
      for (let i = 0; i < saldos.length; i += BATCH) {
        const chunk = saldos.slice(i, i + BATCH);
        const resp = await api.post('/reportes/saldos/snapshot/merge-saldos', {
          snapshot_id: snapshot.id,
          saldos: chunk,
        });
        actualizadas += resp.data?.actualizadas || 0;
        noEncontradas += resp.data?.no_encontradas || 0;
        setImportProgress({ done: Math.min(i + BATCH, saldos.length), total: saldos.length });
      }

      toast.success(`Importadas ${actualizadas} de ${saldos.length} billeteras` +
        (noEncontradas ? ` (${noEncontradas} no estaban en el snapshot)` : ''));
      await loadLatest();
    } catch (err) {
      console.error(err);
      toast.error('Error importando el CSV: ' + (err?.message || 'desconocido'));
    } finally {
      setImporting(false);
    }
  };

  // -------- UI --------
  const syncInProgress = syncJob?.estado === 'EN_PROGRESO' || syncJob?.estado === 'PENDIENTE';
  const saldoInProgress = saldoJob?.estado === 'EN_PROGRESO' || saldoJob?.estado === 'PENDIENTE';
  const visibleColumns = COLUMNS.filter(c => visibleCols[c.key]);

  // Detección de zombies: si lleva más de 90s sin update, asumir que el task murió
  const syncIsZombie = useMemo(() => {
    if (!syncInProgress || !syncJob?.updated_at) return false;
    try {
      const last = new Date(syncJob.updated_at).getTime();
      return (Date.now() - last) > 90_000;
    } catch { return false; }
  }, [syncInProgress, syncJob]);

  // Re-render cada 5s para que syncIsZombie se actualice
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!syncInProgress) return;
    const id = setInterval(() => setTick(t => t + 1), 5000);
    return () => clearInterval(id);
  }, [syncInProgress]);

  return (
    <div className="space-y-4" data-testid="saldos-cuenta-tab">
      {/* Header con stats + botón sync */}
      <Card className="p-4 border-slate-200">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-blue-50">
              <Wallet size={22} className="text-blue-600" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900">Saldos en Cuenta</h3>
              <p className="text-xs text-slate-500">
                {snapshot
                  ? <>Último snapshot: <b>{fmtAR(snapshot.created_at)}</b> · {snapshot.total} billeteras</>
                  : 'Sin datos. Iniciá la primera sincronización.'}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1" data-testid="saldos-exec-mode">
              <button
                onClick={() => setExecMode('local')}
                className={`px-2.5 py-1 text-xs font-medium rounded transition ${
                  execMode === 'local' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-800'
                }`}
                title="Ejecuta en el servidor del pod (rápido si funciona)"
              >
                🖥️ Servidor
              </button>
              <button
                onClick={() => setExecMode('external')}
                className={`px-2.5 py-1 text-xs font-medium rounded transition ${
                  execMode === 'external' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-800'
                }`}
                title="Encola el trabajo para que sea ejecutado por el agente externo (E1)"
              >
                🛰️ Externo
              </button>
            </div>
            <Button
              onClick={startSync}
              disabled={execMode === 'local' && syncInProgress}
              style={{ backgroundColor: '#2563eb', color: '#fff' }}
              data-testid="saldos-sync-btn"
            >
              {execMode === 'local' && syncInProgress
                ? <><Loader2 size={16} className="mr-2 animate-spin" />Sincronizando...</>
                : <><RefreshCw size={16} className="mr-2" />{execMode === 'external' ? 'Encolar Sync (Externo)' : 'Sincronizar Reporte (Paso 1)'}</>}
            </Button>
            <Button
              variant="outline"
              onClick={exportCSV}
              disabled={!sorted.length}
              data-testid="saldos-export-btn"
            >
              <Download size={16} className="mr-2" />
              Exportar CSV
            </Button>
            <Button
              variant="outline"
              onClick={downloadLinks}
              disabled={!items.length}
              data-testid="saldos-download-links-btn"
              title="Descarga un CSV con el link directo a cada billetera (para el script PowerShell)"
            >
              <LinkIcon size={16} className="mr-2" />
              Descargar links (CSV)
            </Button>
            <input
              ref={importFileRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleImportFile}
              className="hidden"
              data-testid="saldos-import-file-input"
            />
            <Button
              variant="outline"
              onClick={() => importFileRef.current?.click()}
              disabled={!items.length || importing}
              data-testid="saldos-import-btn"
              title="Importa el CSV de salida del script PowerShell (waya_saldos.csv) y carga los saldos en la app"
            >
              {importing
                ? <><Loader2 size={16} className="mr-2 animate-spin" />Importando {importProgress.total ? `${importProgress.done}/${importProgress.total}` : '...'}</>
                : <><Upload size={16} className="mr-2" />Importar saldos (CSV)</>}
            </Button>
          </div>
        </div>
        {syncInProgress && (
          <div className={`mt-3 flex items-center gap-2 text-xs px-3 py-2 rounded ${
            syncIsZombie ? 'bg-amber-50 text-amber-800 border border-amber-300' : 'bg-blue-50 text-blue-700'
          }`}>
            {syncIsZombie
              ? <AlertCircle size={14} className="shrink-0 text-amber-600" />
              : <Loader2 size={14} className="animate-spin shrink-0" />}
            <div className="flex-1">
              <div>
                {syncIsZombie ? '⚠️ Job posiblemente colgado · ' : 'Sincronización en progreso · '}
                <b>{syncJob?.fase || 'iniciando'}</b>
                {syncJob?.updated_at && (
                  <span className="text-[10px] opacity-70 ml-2">
                    (últ. update hace {Math.floor((Date.now() - new Date(syncJob.updated_at).getTime()) / 1000)}s)
                  </span>
                )}
              </div>
              {syncJob?.fase_detail && (
                <div className="text-[11px] opacity-80 mt-0.5 truncate" title={syncJob.fase_detail}>
                  {syncJob.fase_detail}
                </div>
              )}
              {syncIsZombie && (
                <div className="text-[11px] mt-1 font-medium">
                  El proceso lleva más de 90s sin avanzar. Probablemente el backend se reinició o Playwright se colgó. Apretá Cancelar y volvé a intentar.
                </div>
              )}
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={abortJobs}
              className={`h-7 px-2 ${syncIsZombie ? 'bg-red-600 text-white hover:bg-red-700' : 'text-red-600 hover:text-red-800 hover:bg-red-50'}`}
              data-testid="saldos-abort-btn"
            >
              <XCircle size={14} className="mr-1" />
              {syncIsZombie ? 'Cancelar (recomendado)' : 'Cancelar'}
            </Button>
          </div>
        )}
        {syncJob?.estado === 'ERROR' && (
          <div className="mt-3 flex items-start gap-2 text-xs text-red-700 bg-red-50 px-3 py-2 rounded">
            <AlertCircle size={14} className="mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="font-medium">Error en sincronización</div>
              <div className="text-[11px] mt-0.5">{syncJob.error || 'desconocido'}</div>
            </div>
          </div>
        )}
      </Card>

      {/* Saldo Job progress */}
      {externalJobs.length > 0 && (
        <Card className="p-3 border-amber-200 bg-amber-50/40" data-testid="saldos-external-jobs-card">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-semibold text-amber-800">🛰️ Cola de ejecución externa</span>
            <Badge className="bg-amber-100 text-amber-800 text-[10px]">
              {externalJobs.filter(j => j.estado === 'PENDIENTE').length} pendiente(s)
            </Badge>
            <Badge className="bg-blue-100 text-blue-800 text-[10px]">
              {externalJobs.filter(j => j.estado === 'CLAIMED').length} en curso
            </Badge>
            <button
              onClick={loadExternalJobs}
              className="ml-auto text-xs text-amber-700 hover:text-amber-900"
              title="Refrescar"
            >
              <RefreshCw size={12} />
            </button>
          </div>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {externalJobs.slice(0, 8).map(j => {
              const badgeColor = j.estado === 'COMPLETADO' ? 'bg-green-100 text-green-700'
                : j.estado === 'ERROR' ? 'bg-red-100 text-red-700'
                : j.estado === 'CLAIMED' ? 'bg-blue-100 text-blue-700'
                : 'bg-amber-100 text-amber-700';
              return (
                <div key={j.id} className="flex items-center justify-between text-[11px] py-1 px-2 bg-white rounded border border-amber-100">
                  <div className="flex items-center gap-2">
                    <Badge className={`${badgeColor} text-[9px]`}>{j.estado}</Badge>
                    <span className="font-mono text-slate-500">{j.id.slice(0, 8)}</span>
                    <span className="text-slate-700 font-medium">{j.tipo}</span>
                    {j.tipo === 'SALDO_LOTE' && j.payload?.ids && (
                      <span className="text-slate-500">({j.payload.ids.length} billeteras)</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-slate-500">
                    {j.estado === 'CLAIMED' && j.progress && (
                      <span>{j.progress.procesados || 0}/{j.progress.total || 0}</span>
                    )}
                    {j.estado === 'COMPLETADO' && j.result && (
                      <span className="text-green-700">
                        {j.result.total ? `${j.result.total} items` : ''}
                        {j.result.actualizadas ? `${j.result.actualizadas} actualizadas` : ''}
                      </span>
                    )}
                    {j.estado === 'ERROR' && (
                      <span className="text-red-600">{(j.result?.error || '').slice(0, 40)}</span>
                    )}
                    <span className="text-[10px]">{fmtAR(j.created_at)}</span>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="text-[10px] text-amber-700 mt-2 italic">
            Tip: cuando encolás un trabajo en modo Externo, pedile al agente E1 que ejecute la cola.
          </div>
        </Card>
      )}

      {/* Saldo Job progress */}
      {saldoJob && (
        <Card className="p-3 border-slate-200" data-testid="saldos-saldo-job-card">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              {saldoInProgress
                ? <Loader2 size={16} className="text-amber-600 animate-spin" />
                : saldoJob.estado === 'COMPLETADO'
                  ? <CheckCircle2 size={16} className="text-green-600" />
                  : <AlertCircle size={16} className="text-red-600" />}
              <span className="text-sm font-medium text-slate-700">
                Saldos detalle (Paso 2): {saldoJob.procesados || 0} / {saldoJob.total || 0}
              </span>
              <Badge className="text-[10px] bg-slate-100 text-slate-700">{saldoJob.estado}</Badge>
              {saldoJob.estado === 'COMPLETADO' && (
                <span className="text-xs text-slate-500">
                  OK={saldoJob.ok} · sin movs={saldoJob.vacios} · errores={saldoJob.errores}
                </span>
              )}
            </div>
            <div className="flex-1 max-w-xs">
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={`h-full ${saldoJob.estado === 'ERROR' ? 'bg-red-500' : 'bg-amber-500'} transition-all`}
                  style={{ width: `${saldoJob.total ? Math.min(100, (saldoJob.procesados / saldoJob.total) * 100) : 0}%` }}
                />
              </div>
            </div>
          </div>
        </Card>
      )}

      <BusquedaMovimientosPanel busquedaNueva={busquedaNueva} />
      <BuscarMovimientosDialog
        open={buscarOpen}
        onOpenChange={setBuscarOpen}
        ids={Array.from(selected)}
        onCreada={setBusquedaNueva}
      />

      {/* Toolbar */}
      {snapshot && (
        <Card className="p-3 border-slate-200">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[240px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Buscar por nombre, email, CUIT, DNI o CVU..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-9 bg-white"
                data-testid="saldos-search-input"
              />
            </div>
            <Select value={cvuFilter} onValueChange={(v) => { setCvuFilter(v); setPage(1); }}>
              <SelectTrigger className="w-44 h-9 bg-white" data-testid="saldos-cvu-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="todas" data-testid="saldos-cvu-filter-todas">CVU: todas</SelectItem>
                <SelectItem value="con" data-testid="saldos-cvu-filter-con">Solo con CVU</SelectItem>
                <SelectItem value="sin" data-testid="saldos-cvu-filter-sin">Solo sin CVU</SelectItem>
              </SelectContent>
            </Select>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="saldos-columns-btn">
                  <Settings2 size={14} className="mr-2" />
                  Columnas ({visibleColumns.length})
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64 max-h-80 overflow-y-auto bg-white">
                <DropdownMenuLabel>Mostrar columnas</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {COLUMNS.map(c => (
                  <DropdownMenuCheckboxItem
                    key={c.key}
                    checked={!!visibleCols[c.key]}
                    onCheckedChange={(v) => setVisibleCols(prev => ({ ...prev, [c.key]: !!v }))}
                  >
                    {c.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Select value={String(pageSize)} onValueChange={(v) => setPageSize(parseInt(v))}>
              <SelectTrigger className="w-32 h-9 bg-white" data-testid="saldos-pagesize-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map(n => (
                  <SelectItem key={n} value={String(n)}>{n} / pág</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="text-xs text-slate-500 ml-auto">
              {sorted.length} {sorted.length === 1 ? 'fila' : 'filas'}
              {items.length !== sorted.length && <> (de {items.length})</>}
            </div>
          </div>

          {/* Acciones de selección (Paso 2) */}
          <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-slate-100">
            <span className="text-xs font-medium text-slate-600">
              Paso 2 — Saldo histórico:
            </span>
            <Badge className="bg-blue-100 text-blue-700 text-[10px]">
              {selected.size} seleccionadas
            </Badge>
            <Button
              size="sm"
              variant="outline"
              onClick={() => startSaldoLote(Array.from(selected))}
              disabled={!selected.size || saldoInProgress}
              data-testid="saldos-procesar-seleccionados-btn"
            >
              <PlayCircle size={14} className="mr-1" />
              Procesar seleccionados ({selected.size})
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" disabled={saldoInProgress} data-testid="saldos-procesar-n-btn">
                  Procesar primeros N...
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-white">
                {[10, 25, 50, 100, 250, 500].map(n => (
                  <DropdownMenuCheckboxItem
                    key={n}
                    checked={false}
                    onCheckedChange={() => {
                      const ids = sorted.slice(0, n).map(i => i.id_billetera).filter(Boolean);
                      startSaldoLote(ids);
                    }}
                  >
                    Primeros {n}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (!window.confirm(`¿Procesar saldo para TODAS las ${sorted.length} billeteras filtradas? Puede tardar mucho tiempo.`)) return;
                const ids = sorted.map(i => i.id_billetera).filter(Boolean);
                startSaldoLote(ids);
              }}
              disabled={!sorted.length || saldoInProgress}
              data-testid="saldos-procesar-todos-btn"
            >
              Procesar TODOS ({sorted.length})
            </Button>
            <Button
              size="sm"
              onClick={() => setBuscarOpen(true)}
              disabled={!selected.size}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="saldos-buscar-movs-btn"
            >
              <Search size={14} className="mr-1" />
              Buscar en movimientos ({selected.size})
            </Button>
            <div className="h-5 w-px bg-slate-200 mx-1" />
            <Button size="sm" variant="ghost" onClick={selectFiltered} disabled={!sorted.length} data-testid="saldos-sel-filtradas-btn">
              Sel. todas filtradas
            </Button>
            <Button size="sm" variant="ghost" onClick={clearSel} disabled={!selected.size} data-testid="saldos-limpiar-sel-btn">
              Limpiar selección
            </Button>
            {[10, 50, 100].map(n => (
              <Button key={n} size="sm" variant="ghost" onClick={() => selectFirstN(n)} data-testid={`saldos-sel-primeros-${n}-btn`}>
                Sel. primeros {n}
              </Button>
            ))}
          </div>
        </Card>
      )}

      {/* Tabla */}
      {loadingSnap ? (
        <Card className="p-12 text-center text-slate-400">
          <Loader2 className="mx-auto animate-spin mb-2" size={24} />
          Cargando snapshot...
        </Card>
      ) : !snapshot ? (
        <Card className="p-12 text-center text-slate-400" data-testid="saldos-empty-state">
          <Wallet size={36} className="mx-auto text-slate-300 mb-2" />
          <p className="text-sm">No hay datos cargados todavía.</p>
          <p className="text-xs mt-1">Apretá <b>Sincronizar Reporte</b> para traer las billeteras de Waya.</p>
        </Card>
      ) : (
        <Card className="border-slate-200 overflow-hidden" data-testid="saldos-table-card">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-2 py-2 w-8">
                    <Checkbox
                      checked={pageItems.length > 0 && pageItems.every(i => selected.has(i.id_billetera))}
                      onCheckedChange={togglePageSel}
                      data-testid="saldos-select-page"
                    />
                  </th>
                  {visibleColumns.map(c => (
                    <th
                      key={c.key}
                      className={`px-2 py-2 font-medium text-slate-600 uppercase text-[10px] tracking-wider ${c.align === 'right' ? 'text-right' : 'text-left'} ${c.sortable ? 'cursor-pointer select-none hover:bg-slate-100' : ''}`}
                      style={{ minWidth: c.width }}
                      onClick={() => onHeaderClick(c)}
                    >
                      <span className="inline-flex items-center gap-1">
                        {c.label}
                        {c.sortable && sortBy.key === c.key && (
                          sortBy.dir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />
                        )}
                      </span>
                    </th>
                  ))}
                  <th className="px-2 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pageItems.map((row) => {
                  const id = row.id_billetera;
                  const sel = id && selected.has(id);
                  return (
                    <tr key={id || row.cvu || row.cuit + row.email} className={sel ? 'bg-blue-50/50' : 'hover:bg-slate-50'}>
                      <td className="px-2 py-1.5">
                        <Checkbox
                          checked={!!sel}
                          onCheckedChange={() => toggleSel(id)}
                          disabled={!id}
                        />
                      </td>
                      {visibleColumns.map(c => {
                        const v = c.get ? c.get(row) : row[c.key];
                        return (
                          <td
                            key={c.key}
                            className={`px-2 py-1.5 text-slate-700 ${c.align === 'right' ? 'text-right tabular-nums' : ''}`}
                          >
                            {c.render ? c.render(v, row) : (v ?? '—')}
                          </td>
                        );
                      })}
                      <td className="px-2 py-1.5">
                        {row.detalle_href && (
                          <a
                            href={`https://administracion.waya.com.ar${row.detalle_href}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-slate-400 hover:text-blue-600"
                            title="Abrir en Waya"
                          >
                            <ExternalLink size={12} />
                          </a>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {pageItems.length === 0 && (
                  <tr>
                    <td colSpan={visibleColumns.length + 2} className="px-4 py-8 text-center text-slate-400 text-xs">
                      Sin resultados con el filtro actual
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          <div className="flex items-center justify-between px-3 py-2 border-t border-slate-100 bg-slate-50/50">
            <div className="text-xs text-slate-500">
              Página {page} de {totalPages} · mostrando {pageItems.length} de {sorted.length}
            </div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" onClick={() => setPage(1)} disabled={page === 1} className="h-7 w-7 p-0"><ChevronsLeft size={14} /></Button>
              <Button size="sm" variant="ghost" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="h-7 w-7 p-0"><ChevronLeft size={14} /></Button>
              <Input
                type="number"
                value={page}
                onChange={(e) => {
                  const n = parseInt(e.target.value);
                  if (!isNaN(n)) setPage(Math.max(1, Math.min(totalPages, n)));
                }}
                className="h-7 w-14 text-center text-xs bg-white"
                data-testid="saldos-page-input"
              />
              <Button size="sm" variant="ghost" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="h-7 w-7 p-0"><ChevronRight size={14} /></Button>
              <Button size="sm" variant="ghost" onClick={() => setPage(totalPages)} disabled={page === totalPages} className="h-7 w-7 p-0"><ChevronsRight size={14} /></Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
